/**
 * Générateur — Il calcule des grandeurs géométriques en utilisant les
 * transformations (symétries, rotations, translations, homothéties).
 * Attendu ID : 236 | Niveau : 3ème | Domaine : Transformations (d19, O6)
 *
 * Source doc prof (GRANDEURS ET MESURES 3e, séances D2S2/D4S6 + Créés
 * n°14-15). Plan auto-validé 2026-07-17 (TODO §6) :
 *   « conservation » — rotation : TOUT est conservé (le théorème d'usage)
 *   « hexagone »     — images dans l'hexagone régulier (mapping de sommets)
 *   « choisir »      — quelle transformation ? (dont le cas IMPOSSIBLE)
 *   « rosace »       — le losange Scratch répété (angles extérieurs, 360/n)
 *   « aires »        — aire × k², y compris k NÉGATIF ((−4)² : LE piège)
 *   « inverse »      — retrouver k depuis les aires (racine carrée)
 *   « emboites »     — composition d'homothéties = produit des rapports
 */

import { randomChoice, randInt, shuffle, tex, makeQcm, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 236;
const L_CONSERVATION = 'Ce que la rotation conserve';
const L_HEXAGONE = 'Trouver une image dans l\'hexagone régulier';
const L_CHOISIR = 'Choisir la bonne transformation';
const L_ROSACE = 'La rosace de losanges (rotation productrice)';
const L_AIRES = 'L\'aire est multipliée par k²';
const L_INVERSE = 'Retrouver le rapport depuis les aires';
const L_EMBOITES = 'Composer deux homothéties';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frNb = (x) => String(Math.round(x * 1000) / 1000).replace('.', ',');

// ═════════ « conservation » ═════════

const genConservationTrous = () => {
    const ab = randInt(3, 9);
    const angle = randInt(38, 132);
    const perim = ab * 2 + randInt(6, 14);
    const aire = randInt(8, 30);
    const transfo = randomChoice(['une rotation', 'une translation', 'une symétrie centrale', 'une symétrie axiale']);
    const phrase = `$A'B' = {{${ab}}}\\ \\text{cm} \\qquad \\widehat{A'B'C'} = {{${angle}}}°`
        + ` \\qquad \\text{périmètre : } {{${perim}}}\\ \\text{cm}`
        + ` \\qquad \\text{aire : } {{${aire}}}\\ \\text{cm}^{2}$`;
    // La règle de conservation N'EST PLUS donnée dans la consigne (elle était
    // écrite entre parenthèses : l'exercice se réduisait à recopier 4 nombres),
    // et le périmètre doit être calculé à partir des quatre côtés.
    const payload = makeTexteTrou(phrase,
        `<p>A'B'C'D' est l'image du quadrilatère ABCD par ${transfo}. On sait que AB = ${ab} cm, `
        + `que l'angle ABC mesure ${angle}°, que le périmètre de ABCD vaut ${perim} cm et son aire ${aire} cm².</p>`
        + '<p><b>Donne A\'B\', l\'angle A\'B\'C\', le périmètre et l\'aire de A\'B\'C\'D\'.</b> '
        + '(Ces transformations conservent longueurs, angles, périmètres et aires !)</p>',
        { ...opts(L_CONSERVATION), mode: 'saisie' });
    payload._meta.params = { ab, angle, perim, aire, transfo };
    return payload;
};

// ═════════ « hexagone » — mapping de sommets ═════════

const SOMMETS = ['A', 'B', 'C', 'D', 'E', 'F'];

const genHexagoneQcm = () => {
    // Triangle X-Y-O ; transformation = décalage d des indices (rotation « qui
    // amène S sur S' », d = 1..5) ou symétrie centrale (d = 3).
    const i = randInt(0, 5);
    const triangle = `${SOMMETS[i]}${SOMMETS[(i + 1) % 6]}O`;
    const centrale = randInt(0, 1) === 1;
    const d = centrale ? 3 : randomChoice([1, 2, 4, 5]);
    const consigne = centrale
        ? 'par la symétrie de centre O'
        : `par la rotation de centre O qui amène ${SOMMETS[0]} sur ${SOMMETS[d]}`;
    const image = `${SOMMETS[(i + d) % 6]}${SOMMETS[(i + 1 + d) % 6]}O`;
    const cand = new Set([image]);
    cand.add(`${SOMMETS[(i + d + 1) % 6]}${SOMMETS[(i + 2 + d) % 6]}O`);
    cand.add(`${SOMMETS[(i + d + 5) % 6]}${SOMMETS[(i + d) % 6]}O`);
    cand.add(triangle);
    const distracteurs = [...cand].filter(x => x !== image);
    const payload = makeQcm([image, ...shuffle(distracteurs).slice(0, 3)], image,
        '<p>ABCDEF est un hexagone RÉGULIER de centre O, dont les sommets sont nommés dans '
        + 'le sens direct.</p>'
        + `<p><b>Quelle est l'image du triangle ${triangle} ${consigne} ?</b></p>`,
        opts(L_HEXAGONE));
    payload._meta.params = { i, d, centrale, triangle, image };
    return payload;
};

// ═════════ « choisir » — dont le cas impossible ═════════

const genChoisirQcm = () => {
    const cas = randomChoice(['translation', 'centrale', 'impossible']);
    // Lettres variables (variabilité) : la géométrie est la même.
    const [p1, p2, p3, p4] = randomChoice([['A', 'B', 'C', 'D'], ['E', 'F', 'G', 'H'], ['M', 'N', 'P', 'Q'], ['R', 'S', 'T', 'U']]);
    const centre = randomChoice(['O', 'I', 'K']);
    const nom = `${p1}${p2}${p3}${p4}`;
    const chTrans = `La translation qui transforme ${p1} en ${p4}.`;
    const chCentrale = `La symétrie de centre ${centre}.`;
    // Sans justification : c'était le SEUL choix à en porter une, donc repérable
    // sans raisonner (même défaut que 211/212, cf. commit calcul littéral).
    const chImp = 'Aucune de ces deux transformations.';
    let question;
    if (cas === 'translation') question = `envoie ${p1} sur ${p4} et ${p2} sur ${p3}`;
    else if (cas === 'centrale') question = `envoie ${p1} sur ${p3} et ${p2} sur ${p4}`;
    else question = `envoie ${p1} sur ${p2} et ${p2} sur ${p4}`;   // côté ≠ diagonale
    const correct = cas === 'translation' ? chTrans : (cas === 'centrale' ? chCentrale : chImp);
    const payload = makeQcm([chTrans, chCentrale, chImp], correct,
        `<p>${nom} est un parallélogramme quelconque de centre ${centre}.</p>`
        // « les vecteurs AD et BC sont égaux, et O est le milieu des diagonales »
        // désignait la bonne réponse : c'est le raisonnement demandé.
        + `<p><b>Quelle transformation ${question} ?</b></p>`,
        opts(L_CHOISIR));
    payload._meta.params = { cas, nom, centre };
    return payload;
};

// ═════════ « rosace » — Scratch ═════════

const genRosaceTrous = () => {
    const alpha = randomChoice([30, 45, 60]);
    const cote = randomChoice([40, 50, 60, 80]);
    const n = randomChoice([6, 8, 9, 10, 12].filter(x => 360 % x === 0));
    const angleRosace = 360 / n;
    const phrase = `avancer de {{${cote}}} ; tourner ↻ de {{${180 - alpha}}}° ; avancer de {{${cote}}} ; tourner ↻ de {{${alpha}}}° — `
        + `puis la rosace : répéter ${n} fois [Losange ; tourner ↻ de {{${angleRosace}}}°].`;
    const payload = makeTexteTrou(phrase,
        `<p>Un losange (angles ${alpha}° et ${180 - alpha}°, côté ${cote} pas) est tracé par un bloc de `
        + 'programme : répéter 2 fois [avancer ; tourner ; avancer ; tourner]. Le lutin tourne des angles '
        + `EXTÉRIEURS et <b>part d'un sommet dont l'angle intérieur mesure ${alpha}°</b> : le premier `
        + 'virage est donc celui qui correspond à ce sommet. '
        + `On répète ensuite le losange ${n} fois autour d'un point pour faire une rosace.</p>`
        // « (360 ÷ nombre de motifs) » donnait le calcul de l'angle de rotation.
        + '<p><b>Complète les valeurs du losange, puis l\'angle de la rotation de la rosace.</b></p>',
        { ...opts(L_ROSACE), mode: 'saisie' });
    payload._meta.params = { alpha, cote, n, angleRosace };
    return payload;
};

// ═════════ « aires » — × k², k négatif inclus ═════════

const genAiresTrous = () => {
    const aire = randomChoice([10, 20, 24, 25, 50]);
    const k10 = randomChoice([2, 4, 5, 6, 8, 12, 15]);     // k = k10/10
    const kNeg = randInt(2, 5);                            // rapport NÉGATIF −kNeg
    // aire × (k10/10)² = aire·k10²/100 : toujours EXACT à 2 décimales.
    const a1 = frNb((aire * k10 * k10) / 100);
    const a2 = aire * kNeg * kNeg;
    const figure = randomChoice(['pentagone', 'hexagone', 'triangle', 'quadrilatère']);
    const phrase = `$\\text{Rapport } ${frNb(k10 / 10).replace(',', '{,}')} \\text{ : aire } {{${a1}}}\\ \\text{cm}^{2}`
        + ` \\qquad \\text{rapport } -${kNeg} \\text{ : aire } {{${a2}}}\\ \\text{cm}^{2}$`;
    const payload = makeTexteTrou(phrase,
        `<p>L'aire d'un ${figure} est ${aire} cm². On en prend l'image par une homothétie.</p>`
        // « L'aire est multipliée par k² » et « (-3)² = 9 » donnaient la règle ET
        // le calcul du cas négatif — c'est-à-dire les deux réponses.
        + '<p><b>Calcule l\'aire de l\'image pour chaque rapport.</b></p>',
        { ...opts(L_AIRES), mode: 'saisie' });
    payload._meta.params = { aire, k10, kNeg };
    return payload;
};

// ═════════ « inverse » — k = √(rapport des aires) ═════════

const genInverseTrous = () => {
    const k10 = randomChoice([2, 4, 5, 6, 8, 12, 15, 20]); // k = k10/10
    let a1;
    for (let essai = 0; essai < 40; essai++) {
        a1 = randomChoice([100, 200, 400, 800, 900]);
        if ((a1 * k10 * k10) % 100 === 0) break;
    }
    const a2 = (a1 * k10 * k10) / 100;
    const k2 = frNb((k10 * k10) / 100);
    const k = frNb(k10 / 10);
    const phrase = `$k^{2} = {{${k2}}} \\quad\\text{donc}\\quad k = {{${k}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Après une homothétie de rapport ${tex('k \\gt 0')}, une figure d'aire ${a1} cm² a une `
        + `image d'aire ${frNb(a2)} cm².</p>`
        // « (rapport des aires) » et « (racine carrée) » étaient les deux étapes.
        + `<p><b>Calcule ${tex('k^{2}')}, puis k.</b> Utilise une virgule pour les décimaux.</p>`,
        { ...opts(L_INVERSE), mode: 'saisie' });
    payload._meta.params = { k10, a1, a2 };
    return payload;
};

// ═════════ « emboites » — composition d'homothéties ═════════

const genEmboitesTrous = () => {
    let c1; let k1x10; let k2; let c2Cent; let c3Cent;
    for (let essai = 0; essai < 60; essai++) {
        c1 = randomChoice([2, 4, 6, 8, 10]);
        k1x10 = randomChoice([15, 20, 25, 30]);            // k1 = 1,5 / 2 / 2,5 / 3
        k2 = randomChoice([2, 3]);
        c2Cent = c1 * k1x10 * 10;                          // côté2 ×100
        c3Cent = c2Cent * k2;
        if (c2Cent % 100 === 0 && c3Cent % 100 === 0 && c3Cent / 100 <= 60) break;
        c3Cent = 0;
    }
    if (!c3Cent) { c1 = 4; k1x10 = 15; k2 = 2; c2Cent = 600; c3Cent = 1200; }
    const c2 = c2Cent / 100; const c3 = c3Cent / 100;
    const kTot = frNb((k1x10 / 10) * k2);
    const aire3 = c3 * c3;
    const phrase = `$\\text{Côté de } C_{2} \\text{ : } {{${frNb(c2)}}}\\ \\text{cm} \\qquad`
        + ` \\text{côté de } C_{3} \\text{ : } {{${frNb(c3)}}}\\ \\text{cm} \\qquad`
        + ` \\text{rapport } C_{1} \\to C_{3} \\text{ : } {{${kTot}}} \\qquad`
        + ` \\text{aire de } C_{3} \\text{ : } {{${aire3}}}\\ \\text{cm}^{2}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Trois carrés : ${tex('C_{2}')} est l'image de ${tex('C_{1}')} par une homothétie de `
        + `rapport ${frNb(k1x10 / 10)}, et ${tex('C_{3}')} est l'image de ${tex('C_{2}')} par une `
        + `homothétie de même centre et de rapport ${k2}. Le côté de ${tex('C_{1}')} mesure ${c1} cm.</p>`
        // « (produit des rapports !) » donnait la composition à découvrir.
        + `<p><b>Calcule les côtés de ${tex('C_{2}')} et ${tex('C_{3}')}, le rapport direct `
        + `${tex('C_{1} \\to C_{3}')}, puis l'aire de ${tex('C_{3}')}.</b></p>`,
        { ...opts(L_EMBOITES), mode: 'saisie' });
    payload._meta.params = { c1, k1x10, k2, c2, c3, aire3 };
    return payload;
};

const GENERATORS = {
    conservation_texte_trou_saisie: genConservationTrous,
    hexagone_qcm_unique: genHexagoneQcm,
    choisir_qcm_unique: genChoisirQcm,
    rosace_texte_trou_saisie: genRosaceTrous,
    aires_texte_trou_saisie: genAiresTrous,
    inverse_texte_trou_saisie: genInverseTrous,
    emboites_texte_trou_saisie: genEmboitesTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'conservation', label: L_CONSERVATION, formatsList: buildFormats('conservation') },
    { id: 'hexagone', label: L_HEXAGONE, formatsList: buildFormats('hexagone') },
    { id: 'choisir', label: L_CHOISIR, formatsList: buildFormats('choisir') },
    { id: 'rosace', label: L_ROSACE, formatsList: buildFormats('rosace') },
    { id: 'aires', label: L_AIRES, formatsList: buildFormats('aires') },
    { id: 'inverse', label: L_INVERSE, formatsList: buildFormats('inverse') },
    { id: 'emboites', label: L_EMBOITES, formatsList: buildFormats('emboites') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
